/**
 * Billing API Service
 * Stripe課金管理APIとの通信
 */

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://struct-code-production.up.railway.app';

class BillingService {
  constructor() {
    this.client = axios.create({
      baseURL: `${API_BASE_URL}/api/billing`,
      headers: { 'Content-Type': 'application/json' },
      timeout: 120000,
    });
  }

  _getAuthHeaders() {
    const token = localStorage.getItem('struct-code-token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  _handleError(error) {
    if (error.response) {
      return { message: error.response.data.detail || 'Server error', status: error.response.status };
    } else if (error.request) {
      return { message: 'Cannot connect to server', status: 0 };
    }
    return { message: error.message || 'Unexpected error', status: -1 };
  }

  async getStatus(slug) {
    try {
      const res = await this.client.get(`/${slug}/status`, { headers: this._getAuthHeaders() });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  async createCheckout(slug) {
    try {
      const res = await this.client.post(`/${slug}/checkout`, {}, { headers: this._getAuthHeaders() });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  async createPortal(slug) {
    try {
      const res = await this.client.post(`/${slug}/portal`, {}, { headers: this._getAuthHeaders() });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  /**
   * 決済完了リダイレクト時に、サーバー経由で Stripe に照会して権限付与を確定させる。
   * webhook の取りこぼしで「決済したのに権限が付かない」状態になるのを防ぐ。
   * 二重に呼んでも upsert なので安全。
   */
  async reconcileCheckout(sessionId) {
    try {
      const res = await this.client.post(
        `/reconcile?session_id=${encodeURIComponent(sessionId)}`,
        {},
        { headers: this._getAuthHeaders() },
      );
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  // --- Individual Pro Subscription ---

  async getIndividualStatus() {
    try {
      const res = await this.client.get('/individual/status', { headers: this._getAuthHeaders() });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  async createIndividualCheckout(planType = 'monthly') {
    try {
      const res = await this.client.post(`/individual/checkout?plan_type=${planType}`, {}, { headers: this._getAuthHeaders() });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  async createIndividualPortal() {
    try {
      const res = await this.client.post('/individual/portal', {}, { headers: this._getAuthHeaders() });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  // --- Category Reports ---

  async createReportCheckout(reportType) {
    try {
      const res = await this.client.post(`/report/checkout?report_type=${reportType}`, {}, { headers: this._getAuthHeaders() });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  async getReport(reportType, lang = 'ja') {
    try {
      const res = await this.client.get(`/report/${reportType}?lang=${lang}`, { headers: this._getAuthHeaders(), timeout: 120000 });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }

  async regenerateReport(reportType, lang = 'ja') {
    try {
      const res = await this.client.post(`/report/${reportType}/regenerate?lang=${lang}`, {}, { headers: this._getAuthHeaders(), timeout: 120000 });
      return res.data;
    } catch (e) { throw this._handleError(e); }
  }
}

const billingService = new BillingService();
export default billingService;
